import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Group } from '@/types/models'
import { toDate } from '@/utils/firestore'

const COLLECTION = 'groups'

function docToGroup(d: { id: string }, data: Record<string, unknown>): Group {
  return {
    id: d.id,
    name: data.name as string,
    description: data.description as string,
    churchId: data.churchId as string,
    color: (data.color as string) || '#3498db',
    isPrivate: (data.isPrivate as boolean) ?? false,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  }
}

export async function getGroups(churchId: string): Promise<Group[]> {
  const q = query(collection(db, COLLECTION), where('churchId', '==', churchId))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docToGroup(d, d.data()))
}

export async function getGroup(id: string): Promise<Group> {
  const snap = await getDoc(doc(db, COLLECTION, id))
  if (!snap.exists()) throw new Error('Group not found')
  return docToGroup(snap, snap.data())
}

export async function addGroup(
  data: Omit<Group, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...data,
    color: data.color || '#3498db',
    isPrivate: data.isPrivate ?? false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateGroup(
  id: string,
  data: Partial<Omit<Group, 'id'>>,
): Promise<void> {
  const update: Record<string, unknown> = {
    ...data,
    updatedAt: serverTimestamp(),
  }
  delete update.createdAt
  await updateDoc(doc(db, COLLECTION, id), update)
}

export async function deleteGroup(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}
